import { AxisModel } from "@syncfusion/ej2-react-spreadsheet";

export type TextAlign = "Left" | "Right" | "Center" | "Justify";
import React, { ReactElement } from "react";
import type { TranslatedString } from "@i18n";

// Custom types replacing syncfusion progressbar types
export interface ILoadedEventArgs {
  progressBar?: any;
  theme?: string;
  cancel?: boolean;
}

export interface FontModel {
  size?: string;
  color?: string;
  fontFamily?: string;
  fontWeight?: string;
  fontStyle?: string;
  opacity?: number;
}

// Chart-related types (migrated from syncfusion to MUI)
export type AccumulationLabelPosition = "Inside" | "Outside";
export interface ConnectorModel {
  length?: string;
  type?: string;
}

export interface TooltipProps {
  enable: boolean;
}

export interface SyncfusionBarChartProps {
  height?: string;
  id: string;
  style: any;
  primaryXAxis: AxisModel;
  primaryYAxis: AxisModel;
  width: string;
  load: (_args: ILoadedEventArgs) => void;
  title: string;
  onChartLoad: (_args: ILoadedEventArgs) => void;
  tooltip: TooltipProps;
  data: any;
}

export enum BorderStyleTypes {
  DOUBLE = "double",
  SOLID = "solid",
  GROOVE = "groove",
  DOTTED = "dotted",
  DASHED = "dashed",
  RIDGE = "ridge",
  INSET = "inset",
}

export enum MaskTypes {
  MOBILE = "000-000-0000",
  COUNTRY_ISO_CODE = "LL/LLL",
  DATE = "00/00/0000",
  PRODUCT_KEY = "AAAAA-AAAAA-AAAAA-AAAAA",
  DATE_FORMAT_2 = "00/LLL/0000",
}

export type ButtonComponentProps = {
  cssClass?: string;
  children?: string | ReactElement<any, any>;
  gradientColor1?: string;
  gradientColor2?: string;
  gradientTextColor1?: string;
  gradientTextColor2?: string;
  gradientBorderColor1?: string;
  gradientBorderColor2?: string;
  bgColor?: string;
  borderColor?: string;
  borderWidth?: string;
  borderType?: BorderStyleTypes;
  btnColor?: string;
  padding?: string;
  borderRadius?: string;
  fontWeight?: string;
  height?: string;
  width?: string;
  disabled?: boolean;
  readOnly?: boolean;
  onBlur?: (data?: any) => any;
  onFocus?: (data?: any) => any;
  onClick?: (data?: any) => any;
  buttonType?: string;
  type?: string;
  textAlign?: any;
};

export interface SplitButtonItemsType {
  id?:string ;
  text?: string;
  iconCss?: string;
}
export type DropdownButtonItemsType = SplitButtonItemsType;
export interface SplitButtonComponentProps {
  ref?:any;
  beforeItemRender?: (data: any) => any;
  items?: SplitButtonItemsType[];
  isDisabled?: boolean;
  content?: string;
  iconCss?: string;
  color?: string;
  gradientColor1?: string;
  gradientColor2?: string;
  gradientTextColor1?: string;
  gradientTextColor2?: string;
  dropdownArrowBgColor?: string;
  bgColor?: string;
  barColor?: string;
  buttonType?: string;
  onBlur?: (data?: any) => any;
  onChange?: (data?: any) => any;
  onClick?: (data?: any) => any;
  onFocus?: (data?: any) => any;
  onSelect?: (data?: any) => any;
}

export interface DropdownButtonComponentProps {
  beforeItemRender?: (data: any) => any;
  items?: DropdownButtonItemsType[];
  children?: string | ReactElement<any, any>;
  isDisabled?: boolean;
  hideIcon?: boolean;
  iconCss?: string;
  color?: string;
  label?: string;
  height?: string;
  gradientColor1?: string;
  gradientColor2?: string;
  bgColor?: string;
  buttonType?: string;
  content?: string;
  popUpWidth?: string;
  maxPopUpHeight?: string;
  width?: string;
  allowButtonPadding?: boolean;
  onSelect?: (data?: any) => any;
  onBlur?: (data?: any) => any;
  onChange?: (data?: any) => any;
  onClick?: (data?: any) => any;
  onFocus?: (data?: any) => any;
  id?:any;
}

export interface DropdownListComponentProps {
  ref?:any
  id?: string;
  isCountryFlag?: boolean;
  dataSource: any;
  textColor?: string;
  popupHeight?: string;
  popupWidth?: string;
  /** Placeholder text - must be wrapped in t() for internationalization */
  placeholder?: TranslatedString;
  fields?: any;
  value: string | null;
  setValue?: (data: any) => void;
  width?: string;
  iconCss?: string;
  color?: string;
  label?: string;
  gradientColor1?: string;
  gradientColor2?: string;
  bgColor?: string;
  buttonType?: string;
  setBackgroundTransparent?: boolean;
  border?: string;
  fontWeightInput?: string;
  allowFlexGrow?: boolean;
  onChange?: (e: any) => void;  
  disabled?:any
  name?:string
}

export interface ToggleSwitchComponentProps {
  id?: string;
  label?: string;
  checked?:boolean;
  isChecked?: boolean;
  isDisabled?: boolean;
  disabled?:boolean;
  gradientColor1?: string;
  gradientColor2?: string;
  bgColor?: string;
  borderColor?: string;
  dragOnColor?: string;
  dragOffColor?: string;
  onBlur?: (data?: any) => any;
  onChange?: (_:any, data?: any) => any;
  onClick?: (data?: any) => any;
  onFocus?: (data?: any) => any;
  change?: (data?: any) => any;
}
export interface CheckboxComponentProps {
  checked?: boolean;
  label?: string;
  borderColor?: string;
  borderColorFocus?: string;
  gradientColor1?: string;
  gradientColor2?: string;
  bgColor?: string;
  name?: string;
  value?: any;
  isDisabled?: boolean;
  onBlur?: (data?: any) => any;
  onChange?: (data?: any) => any;
  onClick?: (data?: any) => any;
  onFocus?: (data?: any) => any;
  change?: (data?: any) => any;
  id?:string
}

export type RadioButtonComponentProps = CheckboxComponentProps;

export interface MultiSelectComponentProps {
  id?: string;
  dataSource?: any;
  mode?: "Box" | "Delimiter" | "Default" | "CheckBox";
  fields?: any;
  /** Placeholder text - must be wrapped in t() for internationalization */
  placeholder?: TranslatedString;
  enabled?: boolean;
  widthInPercentage?: string;
  margin?: string;
  disableChipBackground?: boolean;
  change?: (data?: any) => any;
  onChange?: (data?: any) => any;
  onClick?: (data?: any) => any;
  onFocus?: (data?: any) => any;
}
export interface TextboxComponentProps {
  /** Placeholder text - must be wrapped in t() for internationalization */
  placeholder?: TranslatedString;
  widthInPercentage?: string;
  isDisabled?: boolean;
  isSuccess?: boolean;
  isError?: boolean;
  isWarning?: boolean;
  isRTL?: boolean;
  isSmall?: boolean;
  label?: string;
  margin?: string;
  name?: string;
  value?: string;
  type?: string;
  floatLabelType?: string;
  onChange?: (data?: any) => any;
  onFocus?: (data?: any) => any;
  onBlur?: (data?: any) => any;
  onKeyDown?: (data?: any) => any;
  onKeyUp?: (data?: any) => any;
  change?: (Data?: any) => any;
  ref?:any
}

export interface TextboxMaskComponentProps {
  mask?: MaskTypes;
  isDisabled?: boolean;
  widthInPercentage?: string;
  margin?: string;
  label?: string;
  onChange?: (data?: any) => any;
  onFocus?: (data?: any) => any;
  onBlur?: (data?: any) => any;
}

export interface TextboxNumericComponentProps {
  format?: string;
  value?: number;
  min?: number;
  max?: number;
  step?: number;
  isDisabled?: boolean;
  widthInPercentage?: string;
  widthInPX?: string;
  margin?: string;
  showSpinButton?: boolean;
  onChange?: (data: any) => void;
}

export interface AutoCompleteComponentProps {
  id?: string;
  /** Placeholder text - must be wrapped in t() for internationalization */
  placeholder?: TranslatedString;
  isEnabled?: boolean;
  widthInPercentage?: string;
  dataSource: any;
  change?: (e: any) => any;
}

export type ComboBoxComponentProps = AutoCompleteComponentProps & {
  popupHeight?: string;
  margin?: string;
  dataSource?: any;
  /** Placeholder text - must be wrapped in t() for internationalization */
  placeholder?: TranslatedString;
  value?: string | number;
  isEnabled?: boolean;
};

export interface RichTextEditorComponentProps {
  id?: string;
  enableResize?: boolean;
  showCharCount?: boolean;
  enabled?: boolean;
  height?: string;
  width?: string;
  items?: string[];
  toolbarBgColor?: string;
  iconHoverBgColor?: string;
  iconHoverColor?: string;
  iconSelectedGradientColor1?: string;
  iconSelectedGradientColor2?: string;
  iconColor?: string;
  change?: (data?: any) => any;
  focus?: (data?: any) => any;
  blur?: (data?: any) => any;
  actionBegin?: (data?: any) => any;
  actionComplete?: (data?: any) => any;
  contentHeight?: string;
  padding?: string;
  children?: any;
  allowPadding?:boolean;
}

export interface IconComponentProps {
  name?: string;
  height?: string | number;
  className?: string;
  showGradient?: boolean;
  /** Tooltip text - must be wrapped in t() for internationalization */
  toolTipContent?: TranslatedString;
  position?: string;
  children?: any;
  menuShow?: boolean;
  isVisible?:boolean;
  isEnabled?: boolean;
  type?: string;
  width?: string;
  parentContainerClass?:string;
  dataTestId?:string;
  onClick?: (data?: any) => any;
  color?: string;
  onMouseDown?: (data?: any) => any
}

export interface ListBoxComponentProps {
  onChange?: (data?: any) => any;
  change?: (data?: any) => any;
  dataSource: any;
  selectedIndex?: any;
  listBoxStyle?: any;
  fields?: { text?: string; value?: string };
  itemTemplate?: (item: any) => React.ReactNode;
  height?: string | number;
}
export declare type TooltipPosition =
  | "bottom-end"
  | "bottom-start"
  | "bottom"
  | "left-end"
  | "left-start"
  | "left"
  | "right-end"
  | "right-start"
  | "right"
  | "top-end"
  | "top-start"
  | "top";
export interface TooltipComponentProps {
  position?: TooltipPosition;
  children?: React.ReactNode;
  /** Tooltip content - strings must be wrapped in t() for internationalization */
  content?: TranslatedString | React.ReactElement | null;
  /** Forwarded to the underlying MUI Tooltip: tooltip follows the cursor. */
  followCursor?: boolean;
  /** Forwarded to the underlying MUI Tooltip. */
  slotProps?: { popper?: { sx?: Record<string, unknown> } & Record<string, unknown> } & Record<string, unknown>;
  tipColor?: string;
  color?: string;
  bgColor?: string;
  fontSize?: string;
  width?: string;
  style?: any;
  showTipPointer?:boolean;
  maxHeight?: string;
  disableHoverListener?:boolean;
  arrow?:any
  className?:any
  tooltipStyles?: any
} 

export interface FeedbackToastPositionProps {
  X: string;
  Y: string;
}

export declare type ToastType = "SUCCESS" | "WARNING" | "ERROR";

export interface FeedbackToastComponentProps {
  title?: string;
  position?: FeedbackToastPositionProps;
  onClose?: (data?: any) => any;
  onBeforeOpen?: (data?: any) => any;
  onBeforeClose?: (data?: any) => any;
  isNewestOnTop?: boolean;
  isActionButton?: boolean;
  isShowCloseButton?: boolean;
  timeOut?: number;
  isShowProgressBar?: boolean;
  actionBtnText?: string;
  onActionBtnClick?: (data?: any) => any;
  width?: number;
  onClick?: (data?: any) => any;
  progressBarColor?: string;
  id?: string;
  content?: string;
  type?: ToastType;
}

export interface CardComponentProps {
  dataBound?: any;
  cardBody?: string | ReactElement<any, any>;
  width?: string;
  background?: string;
  cardHeaderText?: string | ReactElement<any, any>;
  headerLink?: string;
  headerLinkText?: string;
  headerBackgroundColor?: string;
  enableShareIcon?: boolean;
  maxMinIconName?: string;
  enableMaxMinIcon?: boolean;
  enableThreeDotsIcon?: boolean;
  items?: any;
  onClick?: any;
  selectDropDownItem?: any;
  onMaximizeClick?: (data?: any) => any;
  onViewCLick?: () => any;
  onListClick?: () => any;
  onItemsClick?: () => any;
  elevation?: number;
}

export declare type AvatarSizeType = "XXS" | "SM" | "LSM" | "XS" | "LG" | "LLG" | "XL";
export interface AvatarComponentProps {
  isCircle?: boolean;
  isSquare?: boolean;
  size?: AvatarSizeType;
  children?: string | ReactElement<any, any>;
  bgColor?: string;
  style?: any;
}

export interface SliderComponentProps {
  onChange?: (data?: any) => any;
  onClick?: (data?: any) => any;
  gradientColor1?: string;
  gradientColor2?: string;
  handleGradientColor1?: string;
  handleGradientColor2?: string;
  handleShadowColor?: string;
  color?: string;
  enabled?: boolean;
  min?: number;
  max?: number;
  value?: number;
  width?: number | string;
}

export interface ModalStateDialogComponentProps {
  headerContent?: TranslatedString | ReactElement<any, any>;
  footerContent?: TranslatedString | ReactElement<any, any>;
  modelContent?: TranslatedString | ReactElement<any, any>;
  isVisible?: boolean;
  width?: string;
  minWidth?: string;
  id?: string;
  ref?: any;
  disableDefaultFooter?: boolean;
  disableCrossIcon?: boolean;
  /** Modal title - strings must be wrapped in t() for internationalization */
  headerTitle?: TranslatedString | ReactElement<any, any>;
  onSave?: () => void;
  onCancel?: () => void;
  allowModalPadding?: boolean;
  modalPadding?: number;
  zindex?: number;
  height?: string | number;
  headerColor?: string;
  footerBorderTop?: boolean;
  footerBorderColor?: string;
}

export interface UserTagComponentProps {
  userId: string;
  isCircle?: boolean;
  borderRadius?: string;
  size?: any;
  iconBgColor?: string;
  userName?: string;
  profileUrl?: string;
  profilePicKey?: string;
  nameInitial?: string;
  fontSize?: string;
  userNameBackground?: string;
  showCrossIcon?: boolean;
  crossIconOnClick?: any;
}

export interface TableComponentProps {
  width?: string;
  body?: any;
  headerColumns: GridHeaderColumnsType[];
  dataSource?: any;
  height?: any;
  field?: any;
  textAlign?: any;
  headerColor?: any;
  queryCellInfo?: any;
  background?: any;
  colorText?: any;
  borderColor?: any;
}

export interface GridHeaderColumnsType {
  field: string;
  headerText: string;
  width?: string;
  textAlign?: TextAlign;
  template?: any;
}

export interface DropdownIconComponentProps {
  dropDownOptions?: any;
  onClick?: (data?: any) => any;
  popUpWidth?: string;
  dataSource?: any;
}

export interface PieChartComponentsProps {
  dataSource: PieChartInfo[];
  dataLabel: PieChartLabelProps;
  pieTitle?: string;
  pieRadius?: string;
  pieExplode?: boolean;
  id: string;
  onTextRender?: (data: any) => any;
  onMouseClick?: (data: any) => any;
  pieRef?: any;
}

export interface PieChartInfo {
  pieXName?: string;
  pieYName?: number;
  pieColor?: string;
}

export interface PieChartLabelProps {
  visible?: boolean;
  position?: AccumulationLabelPosition;
  connectorStyle?: ConnectorModel;
  font?: FontModel;
}

export type ShapeLayerType = "OSM" | "Geometry" | "Bing" | "GoogleStaticMap" | "Google";
export interface SparklineGraphComponentProps {
  id?: any;
  dataSource?: any;
  xName?: any;
  yName?: any;
}
export interface DragListComponentProps {
  data1?: any;
  data2?: any;
  fields?: any;
  setData1?: any;
  setData2?: any;
  itemTemplate?: any;
}

export interface AccordionComponentProps {
  children?: string | ReactElement<any, any>;
  bgColor?: string;
  normal?: string;
  allowBorder?: string;
  allowBorderPadding?: string;
  allowEachDirectiveBorder?: string;
}

export interface AccordionItemModelProps extends Record<string, unknown> {
  props?: any;
}


export interface MapComponentProps {
  latitude?: any;
  longitude?: any;
  zoomFactor?: any;
  enable?: boolean;
  height?: any;
  width?: any;
  name?: any;
  isToolTipVisible?: any;
  mapOnClick?: (data: any) => any;
}
